import {
    ImageType,
    Nullable,
    OffCanvasProductType,
    RecommendedProductType,
    ResourceType,
    StockInformationType
} from "../../definitions/types";
import {ImageModel} from "./imageModel";
import {PriceModel} from "./priceModel";
import {CrossSellModel} from "./crossSellModel";
import {VariantAttributeModel} from "./variantAttributeModel";
import {VariantAttributesModel} from "../view/variantAttributesModel";

export class OffCanvasProductModel {
    private readonly _product: OffCanvasProductType;

    /**
     * @constructor
     * @param product - ProductResponseType
     * @throws Error - if product is null
     * @returns ProductModel
     * @description - Creates an instance of Product class from ProductResponseType object and returns it if product is not null
     */
    constructor(public readonly product: OffCanvasProductType) {
        this._product = product;
    }

    /**
     * @method id
     * @description - Returns the product id or undefined if product is null or id is null or undefined
     * @returns {number}
     */
    public get id(): number {
        const {id} = this._product ?? {id: undefined};
        return id;
    }

    /**
     * @method
     * @returns {number | undefined}
     * @description - Returns artworkGuidelineTypeId or null if artworkGuidelineTypeId is null
     */
    public get artworkGuidelineTypeId(): number | undefined {
        const {artworkGuidelineTypeId} = this._product ?? {artworkGuidelineTypeId: undefined};
        return artworkGuidelineTypeId;
    }

    /**
     * @property artworkGuidelineTypePath
     * @description - Returns undefined if artworkGuidelineTypeId is null or 6,
     * otherwise returns the path to the artwork guideline type details page
     * @returns {string | undefined}
     */
    public get artworkGuidelineTypePath(): string | undefined {
        if (!this.id || !this.artworkGuidelineTypeId || this.artworkGuidelineTypeId === 6) return undefined;
        return `/artworkguideline/${this.artworkGuidelineTypeId}/${this.id}`;
    }

    /**
     * @method
     * @returns {ResourceType[]}
     * @description - Return array of ResourceType or empty array if resources is null
     * @see
     * @example
     * [
     *  {
     *   id: 1,
     *   name: 'resource1',
     *   price: 100,
     *   ...etc. (see ResourceType)
     *  }
     * ]
     */
    public get resources(): ResourceType[] {
        const {resources} = this._product ?? {resources: [] as Nullable<ResourceType>[]};
        return resources.filter((resource): resource is ResourceType => resource !== null);
    }

    /**
     * @method warranties
     * @returns {ResourceType[]}
     * @description - Return array of ResourceType or empty array if there are no warranties in resource array or if resource is null
     */
    public get warranties(): ResourceType[] {
        return this.resources.filter(resource => resource.type === 1 || resource.type === 2);
    }

    /**
     * @method designService
     * @returns {ResourceType | undefined}
     * @description - Returns a ResourceType or undefined if there is no design service resource within the resource array
     */
    public get designService(): ResourceType | undefined {
        return this.resources.find(resource => resource.type === 0);
    }

    /**
     * @method
     * @returns { string }
     * @description - Returns SKU or undefined if SKU is null
     * @see
     * @example
     * 'SKU123'
     */
    public get sku(): string {
        const {sku} = this._product ?? {sku: undefined};
        return sku;
    }

    /**
     * @method
     * @returns Nullable<StockInformationType>
     * @description - Returns array of SpecificationType or empty array if specifications are null.
     *  Not currently in use, but added in case stock information needs to be moved here.
     */
    public get stockInformation(): null | StockInformationType {
        const {stockInformation} = this._product ?? {stockInformation: null};
        return stockInformation
    }

    /**
     * @method title
     * @description - Returns the title of the associated primary, then standalone, then undefined if none
     * @returns {string}
     */
    public get title(): string {
        return this._product.title;
    }

    /**
     * @method
     * @description - Returns all media or empty arrays if media is null
     * @returns { images: { raw: ImageType[]; dot4b: ImageType[] }; videos: { productIntro: VideoType[]; productSeriesShowcase: VideoType[]; productOnly: VideoType[] }; guidelines: { artworkTemplate: GuidelineType[]; instructions: GuidelineType[]; warranty: GuidelineType[] } }
     */
    public get media(): {
        images: { dot4b: ImageModel[] };
    } {
        const {media} = this._product ?? {media: undefined};
        return {
            images: {
                dot4b: media
                    ? media.images.dot4b
                        .filter((image): image is ImageType => image !== null)
                        .map((image) => new ImageModel(image))
                    : [] as ImageModel[]
            }
        };
    }

    /**
     * @property priceModel
     * @description - Returns a PriceModel or undefined if price is null
     * @returns {PriceModel | undefined}
     */
    public get priceModel(): PriceModel | undefined {
        if (!this._product.price || !this._product.stockInformation) return undefined;
        return new PriceModel(this._product.price, this._product.stockInformation);
    }

    /**
     * @property url
     * @description - Returns the url of the product or undefined
     * @returns {string}
     */
    public get url(): string {
        return this._product.variantUrl;
    }

    /**
     * @property recommendations
     * @description - Returns the recommendations of the product
     * @returns {CrossSellModel[]}
     */
    public get recommendations(): CrossSellModel[] {
        const {recommendations} = this._product ?? {recommendations: []};
        return (recommendations?.products ?? [] as Nullable<RecommendedProductType>[])
            .filter((recommendation): recommendation is RecommendedProductType => recommendation !== null)
            .map((recommendation) => new CrossSellModel({
                    ...recommendation,
                    id: recommendation.productId,
                    isDynamicDimension: !!recommendation.dimensionalPrice,
                    minQuantity: recommendation.minQuantity ?? 1,
                    variantPath: recommendation.path
                }, {
                    alt: recommendation.imageAlt,
                    title: recommendation.imageTitle,
                    url: recommendation.imageSrc
                }, {
                    ...recommendation
                },
                recommendation.ratingStatistics));
    }

    /**
     * @property variantAttributes
     * @description - Returns the variant attributes of the product
     * @returns {VariantAttributesModel}
     */
    public get variantAttributes(): VariantAttributesModel {
        return new VariantAttributesModel(this._product.variantAttributes
            .filter((attribute): attribute is VariantAttributeModel => attribute !== null)
            .map((attribute) => new VariantAttributeModel(attribute)));
    }
}